/**
 * Trainova AI — Frontend Permission Checking Hook
 */

import { useAuth } from '../../../stores/authStore';

export const Permissions = {
  PLATFORM_TENANTS_WRITE: 'platform:tenants:write',
  PLATFORM_TENANTS_READ: 'platform:tenants:read',
  PLATFORM_BILLING_MANAGE: 'platform:billing:manage',
  PLATFORM_AUDIT_READ: 'platform:audit:read',
  TENANT_SETTINGS_UPDATE: 'tenant:settings:update',
  TENANT_USERS_MANAGE: 'tenant:users:manage',
  TENANT_AUDIT_READ: 'tenant:audit:read',
  SCENARIO_CREATE_EDIT: 'scenario:create_edit',
  SCENARIO_PUBLISH: 'scenario:publish',
  SCENARIO_READ: 'scenario:read',
  SIMULATION_EXECUTE: 'simulation:execute',
  ATTEMPT_VIEW_OWN: 'attempt:view_own',
  ATTEMPT_VIEW_COHORT: 'attempt:view_cohort',
  EVALUATION_OVERRIDE_SCORE: 'evaluation:override_score',
  ANALYTICS_EXPORT: 'analytics:export_reports',
} as const;

export type PermissionCode = (typeof Permissions)[keyof typeof Permissions];

const RolePermissionMap: Record<string, PermissionCode[]> = {
  PLATFORM_SUPER_ADMIN: Object.values(Permissions),
  SUPER_ADMIN: Object.values(Permissions),
  PLATFORM_ADMIN: [
    Permissions.PLATFORM_TENANTS_WRITE,
    Permissions.PLATFORM_TENANTS_READ,
    Permissions.PLATFORM_BILLING_MANAGE,
    Permissions.PLATFORM_AUDIT_READ,
    Permissions.TENANT_SETTINGS_UPDATE,
    Permissions.TENANT_USERS_MANAGE,
    Permissions.SCENARIO_CREATE_EDIT,
    Permissions.SCENARIO_PUBLISH,
    Permissions.SCENARIO_READ,
    Permissions.SIMULATION_EXECUTE,
    Permissions.ATTEMPT_VIEW_OWN,
    Permissions.ATTEMPT_VIEW_COHORT,
    Permissions.EVALUATION_OVERRIDE_SCORE,
    Permissions.ANALYTICS_EXPORT,
  ],
  PLATFORM_AUDITOR: [
    Permissions.PLATFORM_TENANTS_READ,
    Permissions.PLATFORM_AUDIT_READ,
    Permissions.TENANT_AUDIT_READ,
  ],
  TENANT_OWNER: [
    Permissions.TENANT_SETTINGS_UPDATE,
    Permissions.TENANT_USERS_MANAGE,
    Permissions.TENANT_AUDIT_READ,
    Permissions.SCENARIO_CREATE_EDIT,
    Permissions.SCENARIO_PUBLISH,
    Permissions.SCENARIO_READ,
    Permissions.SIMULATION_EXECUTE,
    Permissions.ATTEMPT_VIEW_OWN,
    Permissions.ATTEMPT_VIEW_COHORT,
    Permissions.EVALUATION_OVERRIDE_SCORE,
    Permissions.ANALYTICS_EXPORT,
  ],
  TENANT_ADMIN: [
    Permissions.TENANT_SETTINGS_UPDATE,
    Permissions.TENANT_USERS_MANAGE,
    Permissions.TENANT_AUDIT_READ,
    Permissions.SCENARIO_CREATE_EDIT,
    Permissions.SCENARIO_PUBLISH,
    Permissions.SCENARIO_READ,
    Permissions.SIMULATION_EXECUTE,
    Permissions.ATTEMPT_VIEW_OWN,
    Permissions.ATTEMPT_VIEW_COHORT,
    Permissions.EVALUATION_OVERRIDE_SCORE,
    Permissions.ANALYTICS_EXPORT,
  ],
  ADMIN: [
    Permissions.TENANT_SETTINGS_UPDATE,
    Permissions.TENANT_USERS_MANAGE,
    Permissions.TENANT_AUDIT_READ,
    Permissions.SCENARIO_CREATE_EDIT,
    Permissions.SCENARIO_PUBLISH,
    Permissions.SCENARIO_READ,
    Permissions.SIMULATION_EXECUTE,
    Permissions.ATTEMPT_VIEW_OWN,
    Permissions.ATTEMPT_VIEW_COHORT,
    Permissions.EVALUATION_OVERRIDE_SCORE,
    Permissions.ANALYTICS_EXPORT,
  ],
  MANAGER: [
    Permissions.SCENARIO_READ,
    Permissions.ATTEMPT_VIEW_OWN,
    Permissions.ATTEMPT_VIEW_COHORT,
    Permissions.ANALYTICS_EXPORT,
  ],
  TRAINER: [
    Permissions.SCENARIO_CREATE_EDIT,
    Permissions.SCENARIO_PUBLISH,
    Permissions.SCENARIO_READ,
    Permissions.SIMULATION_EXECUTE,
    Permissions.ATTEMPT_VIEW_OWN,
    Permissions.ATTEMPT_VIEW_COHORT,
    Permissions.ANALYTICS_EXPORT,
  ],
  INSTRUCTOR: [
    Permissions.SCENARIO_CREATE_EDIT,
    Permissions.SCENARIO_PUBLISH,
    Permissions.SCENARIO_READ,
    Permissions.SIMULATION_EXECUTE,
    Permissions.ATTEMPT_VIEW_OWN,
    Permissions.ATTEMPT_VIEW_COHORT,
    Permissions.ANALYTICS_EXPORT,
  ],
  EVALUATOR: [
    Permissions.SCENARIO_READ,
    Permissions.ATTEMPT_VIEW_OWN,
    Permissions.ATTEMPT_VIEW_COHORT,
    Permissions.EVALUATION_OVERRIDE_SCORE,
  ],
  CANDIDATE: [
    Permissions.SCENARIO_READ,
    Permissions.SIMULATION_EXECUTE,
    Permissions.ATTEMPT_VIEW_OWN,
  ],
  LEARNER: [
    Permissions.SCENARIO_READ,
    Permissions.SIMULATION_EXECUTE,
    Permissions.ATTEMPT_VIEW_OWN,
  ],
  VIEWER: [Permissions.SCENARIO_READ, Permissions.ATTEMPT_VIEW_OWN],
};

export function usePermissions() {
  const { user } = useAuth();
  const role = (user?.role || '').toUpperCase();

  const can = (permission: PermissionCode): boolean => {
    if (!role) return false;
    const perms = RolePermissionMap[role] || [];
    return perms.includes(permission);
  };

  const isPlatformAdmin = ['PLATFORM_SUPER_ADMIN', 'PLATFORM_ADMIN', 'SUPER_ADMIN', 'ADMIN'].includes(role);
  const isTenantAdmin = isPlatformAdmin || ['TENANT_OWNER', 'TENANT_ADMIN', 'ADMIN', 'TRAINER', 'INSTRUCTOR', 'SUPERVISOR', 'MANAGER'].includes(role);
  const canAssign = isTenantAdmin || can(Permissions.SCENARIO_CREATE_EDIT) || can(Permissions.TENANT_USERS_MANAGE);

  return {
    can,
    role,
    isPlatformAdmin,
    isTenantAdmin,
    canAssign,
  };
}
